import {
  DynamicFooter,
  DynamicHeader,
} from "@/components/DynamicComponentRenderer";
import { MetaHead } from "@/components/MetaHead";
import { useConfig } from "@/context/ConfigContext";
import { getDomainFromRequest, getInitialSeoForPage } from "@/lib/serverSeoConfig";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { RiArrowRightSLine } from "react-icons/ri";

const normalizeBlogItem = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.data && typeof data.data === "object") return data.data;
  if (data.blog && typeof data.blog === "object") return data.blog;
  if (!Array.isArray(data)) return data;
  return null;
};

const toPlainText = (value, limit = 160) => {
  if (!value) return "";
  const plain = String(value).replace(/<[^>]*>/g, "").trim();
  if (!plain) return "";
  return plain.length > limit ? plain.slice(0, limit) : plain;
};

const pickBlogImage = (blog) => {
  if (!blog || typeof blog !== "object") return null;
  if (blog.image) return blog.image;
  if (blog.thumbnail) return blog.thumbnail;
  if (Array.isArray(blog.images) && blog.images.length > 0) {
    return blog.images[0];
  }
  return null;
};

export default function BlogDetail({ initialSeo }) {
  const router = useRouter();
  const { slug } = router.query;
  const { config } = useConfig();
  const imageBaseUrl = config?.api?.imageUrl || "";

  // State untuk blog detail
  const [blog, setBlog] = useState(null);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState(null);

  // State untuk related blogs
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [relatedBlogsLoading, setRelatedBlogsLoading] = useState(false);

  // State untuk daftar blog lainnya
  const [otherBlogs, setOtherBlogs] = useState([]);

  useEffect(() => {
    if (!slug) return;
    fetch(`/api/blog?per_page=6`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        let items = Array.isArray(data?.data) ? data.data : [];
        setOtherBlogs(items.filter((b) => b.slug !== slug).slice(0, 5));
      })
      .catch(() => {});
  }, [slug]);

  // Ambil detail blog
  useEffect(() => {
    if (!slug) return;

    let isMounted = true;

    const fetchBlog = async () => {
      try {
        setBlogLoading(true);
        setBlogError(null);

        const res = await fetch(`/api/blog?slug=${slug}`);
        if (!res.ok) {
          throw new Error("Gagal memuat blog");
        }

        const data = await res.json();

        // Normalisasi data
        const blogData = normalizeBlogItem(data);

        if (!isMounted) return;
        setBlog(blogData);

        // Fetch related blogs jika ada category
        if (blogData?.category_id || blogData?.category?.id) {
          const categoryId = blogData.category_id || blogData.category?.id;
          const relatedRes = await fetch(
            `/api/blog?category_id=${categoryId}&per_page=3`
          );
          if (relatedRes.ok) {
            const relatedData = await relatedRes.json();
            let items = [];

            if (Array.isArray(relatedData)) {
              items = relatedData;
            } else if (Array.isArray(relatedData?.data)) {
              items = relatedData.data;
            } else if (Array.isArray(relatedData?.data?.data)) {
              items = relatedData.data.data;
            } else if (Array.isArray(relatedData?.blogs)) {
              items = relatedData.blogs;
            }

            // Filter out current blog
            items = items.filter(
              (item) =>
                (item.id || item.slug) !== (blogData.id || blogData.slug)
            );
            setRelatedBlogs(items.slice(0, 3));
          }
        }
      } catch (error) {
        if (!isMounted) return;
        setBlogError(error?.message || "Terjadi kesalahan saat memuat blog");
      } finally {
        if (!isMounted) return;
        setBlogLoading(false);
        setRelatedBlogsLoading(false);
      }
    };

    fetchBlog();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  // Helper untuk mendapatkan image URL
  const getBlogImageUrl = (blogItem) => {
    if (!blogItem) return "/images/article/article-1.webp";

    const imageSrc =
      blogItem?.image ||
      blogItem?.thumbnail ||
      (Array.isArray(blogItem?.images)
        ? blogItem.images[0]
        : blogItem?.images) ||
      "/images/article/article-1.webp";

    if (imageSrc.startsWith("http://") || imageSrc.startsWith("https://")) {
      return imageSrc;
    }

    if (imageSrc.startsWith("/")) {
      return imageSrc;
    }

    if (imageBaseUrl) {
      const baseUrl = imageBaseUrl.replace(/\/$/, "");
      const imagePath = imageSrc.replace(/^\//, "");
      return `${baseUrl}/${imagePath}`;
    }

    return imageSrc;
  };

  if (blogLoading) {
    return (
      <>
        <MetaHead
          pagePath={`/blog/${slug}`}
          title={initialSeo?.title || "Loading..."}
          description={initialSeo?.description}
          ogImage={initialSeo?.ogImage || undefined}
          ogUrl={initialSeo?.ogUrl}
        />
        <DynamicHeader />
        <div className="overflow-x-hidden pt-16">
          <div className="px-4 py-8 sm:py-12 md:py-20 lg:py-24 sm:px-6 md:px-12 lg:px-24">
            <div className="space-y-6 animate-pulse">
              <div className="h-8 bg-nevada-200 rounded w-3/4" />
              <div className="h-4 bg-nevada-200 rounded w-1/2" />
              <div className="h-64 bg-nevada-200 rounded" />
              <div className="space-y-3">
                <div className="h-4 bg-nevada-200 rounded w-full" />
                <div className="h-4 bg-nevada-200 rounded w-full" />
                <div className="h-4 bg-nevada-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        </div>
        <DynamicFooter />
      </>
    );
  }

  if (blogError || !blog) {
    return (
      <>
        <MetaHead
          pagePath={`/blog/${slug}`}
          title={initialSeo?.title || "Blog Tidak Ditemukan"}
          description={initialSeo?.description}
          ogImage={initialSeo?.ogImage || undefined}
          ogUrl={initialSeo?.ogUrl}
        />
        <DynamicHeader />
        <div className="overflow-x-hidden pt-16">
          <div className="px-4 py-8 sm:py-12 md:py-20 lg:py-24 sm:px-6 md:px-12 lg:px-24">
            <div className="text-center space-y-4">
              <h1 className="text-2xl font-semibold">Blog Tidak Ditemukan</h1>
              <p className="text-nevada-600">
                {blogError || "Blog yang Anda cari tidak ditemukan."}
              </p>
              <Link
                href="/blog"
                className="inline-block px-6 py-3 bg-nevada-950 text-white rounded-lg hover:bg-nevada-800"
              >
                Kembali ke Blog
              </Link>
            </div>
          </div>
        </div>
        <DynamicFooter />
      </>
    );
  }

  const title = blog?.title || blog?.name || "Judul artikel";
  const blogCategories = Array.isArray(blog?.categories) ? blog.categories : [];
  const blogTags = Array.isArray(blog?.tags) ? blog.tags : [];
  const category =
    blogCategories[0]?.name ||
    blog?.category?.name ||
    blogTags[0]?.tags ||
    blog?.category ||
    "Blog";

  let dateText = "";
  const rawDate = blog?.published_at || blog?.created_at || blog?.date || "";
  if (rawDate) {
    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) {
      dateText = d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } else {
      dateText = rawDate;
    }
  }

  const content =
    blog?.content || blog?.body || blog?.description || blog?.excerpt || "";

  const imageSrc = getBlogImageUrl(blog);
  const metaDescription =
    blog?.excerpt ||
    blog?.summary ||
    toPlainText(blog?.description, 160) ||
    initialSeo?.description ||
    "Baca artikel lengkap di blog kami";
  const metaOgImage = imageSrc || initialSeo?.ogImage || undefined;

  return (
    <>
      <MetaHead
        pagePath={`/blog/${slug}`}
        title={`${title} - Blog`}
        description={metaDescription}
        ogImage={metaOgImage}
        ogUrl={initialSeo?.ogUrl}
      />
      <DynamicHeader />
      <div className="overflow-x-hidden">
        <div className="px-4 py-8 sm:py-12 md:py-20 lg:pt-12 lg:pb-24 sm:px-6 md:px-12 lg:px-24 space-y-10 md:space-y-16 mb-16 md:mb-0">
          <div className="space-y-4 md:space-y-6">
            {/* Breadcrumb */}
            <nav
              className="flex flex-wrap items-center gap-2 text-xs sm:text-sm md:text-base"
              aria-label="Breadcrumb"
            >
              <Link href="/" className="font-semibold hover:text-nevada-950">
                Home
              </Link>
              <RiArrowRightSLine />
              <Link
                href="/blog"
                className="font-semibold hover:text-nevada-950"
              >
                Blog
              </Link>
              <RiArrowRightSLine />
              <div className="font-semibold text-nevada-950">{title}</div>
            </nav>
            {/* End Breadcrumb */}

            {/* Responsive Layout */}
            <div className="w-full flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Main Content */}
              <div className="w-full lg:w-8/12 space-y-6">
                <h1 className="font-semibold py-4 text-lg sm:text-xl md:text-2xl lg:text-4xl text-nevada-950">
                  {title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm md:text-base">
                  {blogCategories.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {blogCategories.map((cat) => (
                        <Link
                          key={cat.id || cat.slug}
                          href={`/blog?category=${cat.slug}`}
                          className="bg-nevada-100 text-nevada-800 rounded-full px-3 py-1 hover:bg-nevada-200 transition"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <span>{category}</span>
                  )}
                  {dateText && (
                    <span className="text-nevada-500">{dateText}</span>
                  )}
                </div>

                <hr className="my-2" />

                {/* Featured Image */}
                {imageSrc && (
                  <div className="w-full h-64 sm:h-80 md:h-96 relative rounded-lg overflow-hidden">
                    <Image
                      src={imageSrc}
                      alt={title}
                      fill
                      className="object-cover w-full h-full"
                      sizes="(max-width: 768px) 100vw, 66vw"
                      onError={(e) => {
                        e.target.src = "/images/article/article-1.webp";
                      }}
                    />
                  </div>
                )}

                {/* Content */}
                <div
                  className="text-black dark:text-white text-sm md:text-base leading-relaxed prose prose-nevada max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: content || "Konten tidak tersedia.",
                  }}
                />
              </div>
              {/* Sidebar */}
              <div className="w-full lg:w-4/12 space-y-6">
                {/* Categories */}
                {blogCategories.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-semibold text-base sm:text-lg text-nevada-950">
                      Kategori
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {blogCategories.map((cat) => (
                        <Link
                          key={cat.id || cat.slug}
                          href={`/blog?category=${cat.slug}`}
                          className="bg-white text-nevada-950 border border-nevada-100 rounded-full px-4 py-2 text-xs sm:text-sm hover:border-nevada-950 transition"
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {blogTags.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-semibold text-base sm:text-lg text-nevada-950">
                      Tags
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {blogTags.map((tag) => (
                        <Link
                          key={tag.id || tag.slug}
                          href={`/blog?tag=${tag.slug}`}
                          className="bg-nevada-50 text-nevada-700 border border-nevada-100 rounded-full px-3 py-1 text-xs sm:text-sm hover:border-nevada-950 transition"
                        >
                          #{tag.tags}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Blog Lainnya */}
                {otherBlogs.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-semibold text-base sm:text-lg text-nevada-950">
                      Blog Lainnya
                    </h2>
                    <div className="flex flex-col">
                      {otherBlogs.map((item, idx) => {
                        const itemImage = getBlogImageUrl(item);
                        const itemTitle = item?.title || item?.name || "Judul artikel";
                        const itemSlug = item?.slug || item?.id || idx;
                        const itemCategory = item?.categories?.[0]?.name || item?.category?.name || "";
                        let itemDate = "";
                        const rawItemDate = item?.published_at || item?.created_at || "";
                        if (rawItemDate) {
                          const d = new Date(rawItemDate);
                          if (!isNaN(d.getTime())) {
                            itemDate = d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
                          }
                        }
                        return (
                          <Link
                            key={item?.id || item?.slug || idx}
                            href={`/blog/${itemSlug}`}
                            className="flex items-start gap-3 border-b border-nevada-100 py-3 hover:bg-nevada-50 transition"
                          >
                            <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                              <Image
                                src={itemImage}
                                alt={itemTitle}
                                fill
                                className="object-cover"
                                sizes="64px"
                                onError={(e) => { e.target.src = "/images/article/article-1.webp"; }}
                              />
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="font-semibold text-xs sm:text-sm line-clamp-2 text-nevada-950">
                                {itemTitle}
                              </div>
                              {(itemCategory || itemDate) && (
                                <div className="text-xs text-nevada-500">
                                  {itemCategory && <span>{itemCategory}</span>}
                                  {itemCategory && itemDate && <span> · </span>}
                                  {itemDate && <span>{itemDate}</span>}
                                </div>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Related Posts */}
                {relatedBlogs.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-semibold text-base sm:text-lg text-nevada-950">
                      Artikel Terkait
                    </h2>
                    <div className="flex flex-col gap-2">
                      {relatedBlogs.map((relatedPost, idx) => {
                        const relatedImage = getBlogImageUrl(relatedPost);
                        const relatedTitle =
                          relatedPost?.title ||
                          relatedPost?.name ||
                          "Judul artikel";
                        const relatedSlug =
                          relatedPost?.slug || relatedPost?.id || idx;

                        return (
                          <Link
                            key={relatedPost?.id || relatedPost?.slug || idx}
                            href={`/blog/${relatedSlug}`}
                            className="flex items-center gap-3 sm:gap-4 border-b border-nevada-100 py-4 sm:py-6 hover:bg-nevada-50 transition"
                          >
                            <Image
                              src={relatedImage}
                              alt={relatedTitle}
                              width={48}
                              height={48}
                              className="rounded-lg w-12 h-12 sm:w-[60px] sm:h-[60px] object-cover flex-shrink-0"
                              onError={(e) => {
                                e.target.src = "/images/article/article-1.webp";
                              }}
                            />
                            <div>
                              <div className="font-semibold text-xs sm:text-sm md:text-base line-clamp-2">
                                {relatedTitle}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* End Responsive Layout */}
          </div>
        </div>
      </div>
      <DynamicFooter />
    </>
  );
}

export async function getServerSideProps(context) {
  const slugParam = context.params?.slug;
  const slug = Array.isArray(slugParam) ? slugParam[0] : slugParam;
  const pagePath = slug ? `/blog/${slug}` : "/blog";

  if (!slug) {
    const initialSeo = await getInitialSeoForPage(context.req, "/blog", {
      title: "Blog",
    });
    return { props: { initialSeo: initialSeo || null } };
  }

  const domain = getDomainFromRequest(context.req);
  const apiBase = (
    process.env.API_URL_V2 ||
    process.env.API_URL ||
    "https://dev.tukuya.com"
  ).replace(/\/+$/, "");

  let initialSeo = await getInitialSeoForPage(context.req, pagePath);

  try {
    const res = await fetch(
      `${apiBase}/api/estore/blog/${encodeURIComponent(slug)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-API-Key": process.env.API_URL_X_API_KEY || "",
          "X-Origin-Domain": domain || "",
        },
      },
    );

    if (res.ok) {
      const payload = await res.json();
      const blog = normalizeBlogItem(payload);
      const blogTitle = blog?.title || blog?.name || null;
      const blogDescription =
        blog?.excerpt ||
        blog?.summary ||
        toPlainText(blog?.description, 160) ||
        toPlainText(blog?.content, 160) ||
        null;
      const blogImage = pickBlogImage(blog);

      initialSeo = await getInitialSeoForPage(context.req, pagePath, {
        title: blogTitle ? `${blogTitle} - Blog` : "Blog",
        description: blogDescription,
        ogImage: blogImage,
      });
    }
  } catch (error) {
    console.error("[BlogDetail][getServerSideProps] Failed to prefetch SEO:", error);
  }

  return {
    props: {
      initialSeo: initialSeo || null,
    },
  };
}
